import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../lib/animations';
import FeedCard, { FeedItem } from '../components/FeedCard';
import AnimatedButton from '../components/AnimatedButton';
import Leaderboard from './Leaderboard';
import HallOfShame from './HallOfShame';

const TABS = [
  { key: 'feed', label: 'Feed' },
  { key: 'rankings', label: 'Rankings' },
  { key: 'shame', label: 'Hall of Shame' },
] as const;

type Tab = (typeof TABS)[number]['key'];

function FeedContent() {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = async (p: number, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await fetch(`/api/feed?page=${p}&pageSize=20`);
      const data = await res.json();
      setItems(prev => append ? [...prev, ...data.items] : data.items);
      setHasMore(data.hasMore);
      setPage(data.page);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchPage(1); }, []);

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-cannabis-800 rounded-2xl border border-cannabis-700/30 p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full skeleton" />
              <div className="h-3 w-24 skeleton" />
              <div className="ml-auto h-3 w-12 skeleton" />
            </div>
            <div className="w-full h-[120px] skeleton rounded-xl mb-2" />
            <div className="flex items-start gap-2 mb-2">
              <div className="h-7 w-10 skeleton" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-full skeleton" />
                <div className="h-3 w-2/3 skeleton" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-cream/40 text-base mb-3">No ratings yet! Be the first to rate a joint.</p>
        <Link
          to="/rate"
          className="inline-block px-5 py-2.5 bg-cannabis-600 hover:bg-cannabis-500 text-cream rounded-xl font-semibold transition text-sm"
        >
          Rate a Joint
        </Link>
      </div>
    );
  }

  return (
    <>
      <motion.div
        className="space-y-2"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        {items.map(item => (
          <motion.div key={item.id} variants={staggerItem}>
            <FeedCard item={item} />
          </motion.div>
        ))}
      </motion.div>
      {hasMore && (
        <div className="mt-4 text-center">
          <AnimatedButton
            onClick={() => fetchPage(page + 1, true)}
            disabled={loadingMore}
            variant="secondary"
            loading={loadingMore}
          >
            Load More
          </AnimatedButton>
        </div>
      )}
    </>
  );
}

export default function Feed() {
  const [activeTab, setActiveTab] = useState<Tab>('feed');

  return (
    <div className="max-w-4xl mx-auto px-3 py-4 space-y-4">
      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`relative px-5 py-2.5 text-sm font-semibold rounded-xl whitespace-nowrap transition-colors ${
              activeTab === tab.key ? 'text-[#4ade80]' : 'text-white/40 hover:text-white/60'
            }`}
          >
            {tab.label}
            {activeTab === tab.key && (
              <motion.div
                layoutId="feed-tab-indicator"
                className="absolute inset-0 bg-[#4ade80]/10 border border-[#4ade80]/20 rounded-xl -z-10"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'feed' && <FeedContent />}
          {activeTab === 'rankings' && <Leaderboard />}
          {activeTab === 'shame' && <HallOfShame />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
